import type { JSX } from 'react';

import ButtonAction from '../components/button-action/ButtonAction';
import Headline from '../components/headline/Headline';
import IconedList from '../components/iconed-list/IconedList';
import LinkAction from '../components/link-action/LinkAction';
import Paragraph from '../components/paragraph/Paragraph';
import Tagline from '../components/tagline/Tagline';
import type { Action, Content, ContentText, ContentView } from '../global-types/Content';

type ActionsConfig = {
  skipFirstAction?: boolean;
  dispatchActionEvent: (action: Action, nextContent?: Content | null) => void;
  validateAction?: (action: Action) => boolean;
  disableButton: boolean;
  setDisableButton: (v: boolean) => void;
};

type RenderContentProps = {
  view?: ContentView;
  texts?: ContentText[];
  trackVisibilityElement?: React.RefObject<null>;
  theme?: string;
  actionsConfig?: ActionsConfig;
};

type ListGroup = { type: 'list-group'; items: string[] };
type GroupedItems = ContentText | ListGroup;

function groupListItems(raw: (ContentText | undefined)[]): GroupedItems[] {
  const result: GroupedItems[] = [];
  let i = 0;
  while (i < raw.length) {
    const t = raw[i];
    if (!t) {
      i++;
      continue;
    }
    if (t.type === 'list') {
      const items: string[] = [];
      while (i < raw.length && raw[i]?.type === 'list') {
        items.push((raw[i] as ContentText).value);
        i++;
      }
      result.push({ type: 'list-group', items });
    } else {
      result.push(t);
      i++;
    }
  }
  return result;
}

function renderTextItems(
  items: GroupedItems,
  index: number,
  isTrackingHeadline: boolean,
  trackVisibilityElement: React.RefObject<null> | undefined,
  theme: string | undefined,
): JSX.Element {
  if (items.type === 'list-group') {
    return <IconedList key={`list-${index}`} listItems={items.items} theme={theme} />;
  }
  if (items.type === 'paragraph') {
    return <Paragraph key={`p-${index}-${items.value}`} text={items.value} size={items.size} color={items.color} />;
  }
  if (items.type === 'headline') {
    return (
      <Headline
        key={`h-${index}-${items.value}`}
        text={items.value}
        ref={isTrackingHeadline ? trackVisibilityElement : undefined}
        size={items.size}
        child={items.child}
        animation={items.animation}
      />
    );
  }
  if (items.type === 'tagline') {
    return (
      <Tagline
        key={`tag-${index}-${items.value}`}
        text={items.value}
        color={items.color}
        size={items.size ?? undefined}
        icon={items.icon}
      />
    );
  }
  if ((items.type as string) === 'price') {
    return (
      <span className="if typography ui price" key={`price-${index}`}>
        {items.value}
      </span>
    );
  }
  return <></>;
}

function renderActionItems(
  view: ContentView,
  actionsConfig: ActionsConfig,
): { linkStyledActions: JSX.Element[]; buttonStyledActions: JSX.Element[] } {
  const linkStyledActions: JSX.Element[] = [];
  const buttonStyledActions: JSX.Element[] = [];
  const list = view.viewElements.actions ?? [];
  const startIndex = actionsConfig.skipFirstAction ? 1 : 0;

  for (let i = startIndex; i < list.length; i++) {
    const action = list[i];
    const key = `${action.actionType}-${action.text}-${i}`;

    if (action.url) {
      const linkAction = (
        <LinkAction key={key} action={action} dispatchActionEvent={actionsConfig.dispatchActionEvent} />
      );
      if (action.actionType === 'button') {
        buttonStyledActions.push(linkAction);
      } else {
        linkStyledActions.push(linkAction);
      }
    } else {
      buttonStyledActions.push(
        <ButtonAction
          key={key}
          action={action}
          dispatchActionEvent={actionsConfig.dispatchActionEvent}
          validateAction={actionsConfig.validateAction}
          disabled={actionsConfig.disableButton}
          setDisabled={actionsConfig.setDisableButton}
        />,
      );
    }
  }

  return { linkStyledActions, buttonStyledActions };
}

export function renderContent({
  view,
  texts: textsElements,
  trackVisibilityElement,
  theme,
  actionsConfig,
}: RenderContentProps) {
  const raw = textsElements ?? (view?.viewElements.texts as ContentText[] | undefined) ?? [];
  const grouped = groupListItems(raw);

  const texts: JSX.Element[] = [];
  let trackHeadline = true;

  for (const [index, element] of grouped.entries()) {
    texts.push(renderTextItems(element, index, trackHeadline, trackVisibilityElement, theme));
    if (element.type === 'headline') {
      trackHeadline = false;
    }
  }

  const { linkStyledActions, buttonStyledActions } =
    actionsConfig && view
      ? renderActionItems(view, actionsConfig)
      : { linkStyledActions: [] as JSX.Element[], buttonStyledActions: [] as JSX.Element[] };

  return { texts, linkStyledActions, buttonStyledActions };
}
